import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, Check, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUpgradeTracking } from '@/hooks/useUpgradeTracking';

/**
 * UpgradePromptModal (Iter 73i — konsistent zu UpgradeRequiredView)
 * Action-Level-Gating-Modal: erscheint, wenn ein Free-/Basic-User ein Pro-Feature
 * versucht (z.B. KI-Beleg-Erkennung). Layout entspricht der Page-Level-Card,
 * nur kompakter im Modal.
 */
export function UpgradePromptModal({
  isOpen,
  onClose,
  feature = 'Beleg-Upload',
  source = 'modal',
  features = [
    'Automatische Beleg-Erkennung (KI)',
    'PDF-Upload bis 25 MB',
    'Felder werden automatisch ausgefüllt',
    'Vorschau-Thumbnails',
  ],
}) {
  const navigate = useNavigate();
  const track = useUpgradeTracking();

  useEffect(() => {
    if (isOpen) track('view', source);
  }, [isOpen, source, track]);

  const handleUpgrade = () => {
    track('click', source);
    onClose();
    navigate('/pricing');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="upgrade-prompt-modal">
        <div className="bg-gradient-to-br from-emerald-50 via-white to-emerald-50 px-6 pt-6 pb-5 border-b border-emerald-100">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold tracking-wider text-emerald-700 bg-emerald-100/70 rounded-full px-2.5 py-0.5">
                ivoliq Pro
              </span>
            </div>
            <DialogTitle className="text-lg font-bold text-slate-900">
              {feature}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mt-1.5">
            Diese Funktion ist Teil von ivoliq Pro.
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <ul className="space-y-2">
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              onClick={handleUpgrade}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="upgrade-modal-cta-primary"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              Auf Pro upgraden
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <Button variant="outline" onClick={onClose} className="sm:flex-shrink-0" data-testid="upgrade-modal-cancel">
              Später
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradePromptModal;